import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ThumbsUp, Copy, Users, Globe, MessageSquare, Calendar, Server as ServerIcon } from 'lucide-react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import { getAuth } from '../utils/auth';
import confetti from 'canvas-confetti';
import { Turnstile } from "@marsidev/react-turnstile";

export default function ServerDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = getAuth();
  const [server, setServer] = useState(null);
  const [canVote, setCanVote] = useState(true);
  const [voteInfo, setVoteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);

  // 🔒 Turnstile token
  const [turnstileToken, setTurnstileToken] = useState(null);

  useEffect(() => {
    fetchServer();
    checkVoteStatus();
  }, [slug]);

  const fetchServer = async () => {
    try {
      const response = await api.get(`/servers/${slug}`);
      setServer(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Server not found');
      navigate('/servers');
    }
  };

  const checkVoteStatus = async () => {
    try {
      const response = await api.get(`/servers/${slug}/can-vote`);
      setCanVote(response.data.can_vote);
      setVoteInfo(response.data);
    } catch (error) {
      console.error('Error checking vote status:', error);
    }
  };

  const handleVote = async () => {
    if (!turnstileToken) {
      toast.error("Please complete verification first!");
      return;
    }

    setVoting(true);
    try {
      const response = await api.post(`/servers/${slug}/vote`, {
        turnstile_token: turnstileToken
      });

      toast.success('Vote successful!');

      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#00E055', '#0CB7E1', '#FFFFFF']
      });

      setServer({ ...server, vote_count: response.data.new_vote_count });
      setCanVote(false);
      setTurnstileToken(null);
      checkVoteStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vote');
    } finally {
      setVoting(false);
    }
  };

  const handleCopyIP = () => {
    navigator.clipboard.writeText(`${server.ip_address}:${server.port}`);
    toast.success('IP Address copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="w-12 h-12 border-4 border-[#00E055] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!server) return null;

  return (
    <div className="min-h-screen noise-bg">
      <Navbar />
      
      <div className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-12">
          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Header Info */}
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-black tracking-tighter uppercase mb-4" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid="server-name">
                {server.name}
              </h1>
              <div className="flex flex-wrap gap-3">
                <span 
                  className="px-4 py-1.5 text-xs font-black tracking-widest uppercase bg-[#00E055]/10 text-[#00E055] border border-[#00E055]/20 rounded-sm flex items-center gap-2 shadow-[0_0_15px_rgba(0,224,85,0.05)]"
                  style={{ fontFamily: 'Unbounded, sans-serif' }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00E055] shadow-[0_0_5px_#00E055]" />
                  {server.category}
                </span>
                <span 
                  className="px-4 py-1.5 text-xs font-bold tracking-widest uppercase bg-white/5 text-white/50 border border-white/10 rounded-sm"
                  style={{ fontFamily: 'Unbounded, sans-serif' }}
                  data-testid="server-version"
                >
                  {server.version}
                </span>
                {server.is_online ? (
                  <span 
                    className="px-4 py-1.5 text-xs font-black tracking-widest uppercase bg-[#00E055]/10 text-[#00E055] border border-[#00E055]/20 rounded-sm flex items-center gap-2 shadow-[0_0_15px_rgba(0,224,85,0.05)]"
                    style={{ fontFamily: 'Unbounded, sans-serif' }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00E055] animate-pulse shadow-[0_0_5px_#00E055]" />
                    ONLINE
                  </span>
                ) : (
                  <span 
                    className="px-4 py-1.5 text-xs font-black tracking-widest uppercase bg-red-500/10 text-red-400 border border-red-500/20 rounded-sm"
                    style={{ fontFamily: 'Unbounded, sans-serif' }}
                  >
                    OFFLINE
                  </span>
                )}
              </div>
            </div>

            {/* Vote Section */}
            <div className="flex flex-col gap-3">
              
              {/* 🔒 Cloudflare Turnstile */}
              <Turnstile
                siteKey="0x4AAAAAACkTt0EiZYxlXrZH"
                onSuccess={(token) => setTurnstileToken(token)}
              />

              <Button
                onClick={handleVote}
                disabled={!canVote || voting}
                size="lg"
                className="bg-[#00E055] hover:bg-[#00E055]/90 text-black font-bold tracking-wide uppercase glow-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ThumbsUp className="w-5 h-5 mr-2" />
                {canVote ? 'VOTE NOW' : 'VOTED'}
              </Button>

              {!canVote && voteInfo && (
                <p className="text-xs text-muted-foreground text-center">
                  Next vote in {Math.floor(voteInfo.seconds_remaining / 3600)}h {Math.floor((voteInfo.seconds_remaining % 3600) / 60)}m
                </p>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-[#0A0A0A] border border-border rounded-lg p-6">
              <div className="flex items-center gap-2 text-[#00E055] mb-2">
                <ThumbsUp className="w-5 h-5" />
                <span className="text-sm font-bold uppercase">VOTES</span>
              </div>
              <p className="text-3xl font-black">{server.vote_count}</p>
            </div>

            {server.player_count !== null && (
              <div className="bg-[#0A0A0A] border border-border rounded-lg p-6">
                <div className="flex items-center gap-2 text-[#0CB7E1] mb-2">
                  <Users className="w-5 h-5" />
                  <span className="text-sm font-bold uppercase">PLAYERS</span>
                </div>
                <p className="text-3xl font-black">{server.player_count}</p>
              </div>
            )}

            <div className="bg-[#0A0A0A] border border-border rounded-lg p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <ServerIcon className="w-5 h-5" />
                <span className="text-sm font-bold uppercase">SERVER IP</span>
              </div>
              <p className="text-sm font-mono mb-2">{server.ip_address}:{server.port}</p>
              <Button onClick={handleCopyIP} size="sm" variant="outline" className="w-full">
                <Copy className="w-3 h-3 mr-2" />
                COPY
              </Button>
            </div>

            <div className="bg-[#0A0A0A] border border-border rounded-lg p-6">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Calendar className="w-5 h-5" />
                <span className="text-sm font-bold uppercase">JOINED</span>
              </div>
              <p className="text-sm">{new Date(server.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Description */}
          <div className="mt-8 bg-[#0A0A0A] border border-border rounded-lg p-8">
            <h2 className="text-2xl font-bold tracking-tight mb-4" style={{fontFamily: 'Unbounded, sans-serif'}}>
              ABOUT THIS SERVER
            </h2>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {server.description}
            </p>
          </div>

          {/* Links */}
          {(server.website || server.discord) && (
            <div className="mt-8 flex flex-wrap gap-4">
              {server.website && (
                <a href={server.website} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="gap-2">
                    <Globe className="w-4 h-4" />
                    VISIT WEBSITE
                  </Button>
                </a>
              )}
              {server.discord && (
                <a href={server.discord} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="gap-2">
                    <MessageSquare className="w-4 h-4" />
                    JOIN DISCORD
                  </Button>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
